import styled from "styled-components";

import { faTriangleExclamation as faTriangleExclamationOutline } from "@fortawesome/pro-regular-svg-icons";
import { faArrowsRotate, faCircleExclamation, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button from "@ntemarked/mittne-components/dist/Button/Button";

/** coral er for meldinger som ennå ikke er vurdert som advarsel, orange for advarsler brukeren kan gå videre forbi. */
type WarningTone = "coral" | "orange";

const WARNING_TONES = {
	coral: { border: colors.coral800, background: colors.coral100, heading: colors.coral, body: colors.grey800, icon: faTriangleExclamation },
	orange: { border: colors.orange500, background: colors.orange100, heading: colors.orange900, body: colors.grey900, icon: faTriangleExclamationOutline },
} as const;

export const BoxedCard = styled.div`
	max-width: 640px;
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.grey300};
	padding: 24px;
`;

const ErrorCard = styled(BoxedCard)`
	box-shadow: inset 0 0 0 1px ${colors.coral800};
	padding: 20px 24px;
	text-align: left;
`;

const ErrorHeading = styled.div`
	font-weight: 700;
	font-size: 1rem;
	color: ${colors.coral};
	margin-bottom: 6px;
`;

const ErrorBody = styled.p`
	color: ${colors.grey800};
	margin: 0 0 14px;
`;

const WarningCard = styled(BoxedCard)<{ $tone: WarningTone }>`
	box-shadow: inset 0 0 0 1px ${({ $tone }) => WARNING_TONES[$tone].border};
	background: ${({ $tone }) => WARNING_TONES[$tone].background};
	padding: 16px 22px;
`;

const WarningHeading = styled(ErrorHeading)<{ $tone: WarningTone }>`
	color: ${({ $tone }) => WARNING_TONES[$tone].heading};
`;

const WarningBody = styled(ErrorBody)<{ $tone: WarningTone }>`
	color: ${({ $tone }) => WARNING_TONES[$tone].body};
	margin: 0;
`;

const EmptyTitle = styled.p`
	margin: 0;
	font-size: 1rem;
	color: ${colors.grey800};
`;

const EmptyHint = styled.p`
	margin: 6px 0 0;
	font-size: 0.875rem;
	color: ${colors.grey700};
`;

export function EmptyBox({ title, hint }: Readonly<{ title: string; hint?: string }>) {
	return (
		<BoxedCard style={{ textAlign: "center" }}>
			<EmptyTitle>{title}</EmptyTitle>
			{hint && <EmptyHint>{hint}</EmptyHint>}
		</BoxedCard>
	);
}

export function ErrorBox({ heading, body, onRetry }: Readonly<{ heading: string; body: string; onRetry?: () => void }>) {
	return (
		<ErrorCard>
			<ErrorHeading>
				<FontAwesomeIcon icon={faCircleExclamation} style={{ marginRight: "8px" }} />
				{heading}
			</ErrorHeading>
			<ErrorBody>{body}</ErrorBody>
			{onRetry && <Button secondary type="button" icon={<FontAwesomeIcon icon={faArrowsRotate} />} label="Prøv igjen" onClick={onRetry} />}
		</ErrorCard>
	);
}

export function WarningBox({ heading, body, tone = "coral" }: Readonly<{ heading: string; body: string; tone?: WarningTone }>) {
	return (
		<WarningCard role="alert" $tone={tone}>
			<WarningHeading $tone={tone}>
				<FontAwesomeIcon icon={WARNING_TONES[tone].icon} style={{ marginRight: "8px" }} />
				{heading}
			</WarningHeading>
			<WarningBody $tone={tone}>{body}</WarningBody>
		</WarningCard>
	);
}
